import { useState, useEffect } from 'react'
import { Table, Card, Input, Select, Space, Tag, Typography, Row, Col, Tooltip, Lightbox } from 'antd'
import { SearchOutlined } from '@ant-design/icons'
import config from '../../../config'

// ** Store Imports
import { useDispatch } from 'react-redux'

// ** Third Party Components
import * as API from "../../../../src/utils/helper/enum";
import globalRequest from "../../../utils/global/globalRequest";
import { changeLoader } from "../../../redux/reducers/loader";
import toast from 'react-hot-toast'
import ImageViewerModal from '../../../Modal/ImageViewerModal'

const { Title } = Typography
const { Option } = Select


const vendorTypeObj = {
  host: { color: 'green', text: 'Host' },
  seller: { color: 'green', text: 'Seller' },
}

let timer = null

const VendorEnquiryList = () => {
  // ** Hooks
  const dispatch = useDispatch()
  
  // ** State
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`
  })
 
  const [sorter, setSorter] = useState({
    field: '',
    order: ''
  })

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState(null);

  const handleOpen = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setIsModalVisible(true);
  };

  const handleClose = () => {
    setIsModalVisible(false);
    setSelectedImageUrl(null);
  };

  const getVendorEnquiryList = async () => {
    try {
      setLoading(true)
      dispatch(changeLoader(true))
      
      const params = {
        page: pagination.current,
        limit: pagination.pageSize,
        orderBy: sorter.field,
        orderType: sorter.order === 'ascend' ? 'ASC' : sorter.order === 'descend' ? 'DESC' : ''
      }

      let responseApi = await globalRequest(
         API?.MANAGE_CMS?.GET_VENDOR_ENQUIRY_LIST, 
        "GET", 
        {}, 
        { params }, 
        true
      )
      
      let responseData = responseApi?.data
      
      if (responseData?.status === "SUCCESS") {
        const enquiries = (responseData?.data?.data || []).map((item) => ({
          ...item,
          businessCategoryName:
            item?.businessCategory?.locales?.[0]?.name || '-',
        }));
        const paginator = responseData?.data?.paginator || {}
        
        // Get total from your paginator structure
        const total = paginator?.itemCount || paginator?.count || 0
        
        setData(enquiries)
        setPagination(prev => ({
          ...prev,
          total: total
        }))
      } else {
        setData([])
        setPagination(prev => ({ ...prev, total: 0 }))
      }
    } catch (e) {
      toast.error(e?.message || 'Failed to fetch vendor enquiries')
      setData([])
    } finally {
      setLoading(false)
      dispatch(changeLoader(false))
    }
  }

  useEffect(() => {
    clearTimeout(timer)
    timer = setTimeout(() => {
      getVendorEnquiryList()
    }, 400)
  }, [pagination.current, pagination.pageSize, sorter])

  const handleTableChange = (paginationConfig, tableSorter) => {
    setPagination(prev => ({
      ...prev,
      current: paginationConfig.current,
      pageSize: paginationConfig.pageSize
    }))

    setSorter({
      field: tableSorter.field,
      order: tableSorter.order
    })
  }

  const columns = [
    {
      title: 'Brand Name',
      dataIndex: 'brandName',
      key: 'brandName',
      sorter: true,
      width: 150,
      render: (text) => (
        <Typography.Text strong>
          {text || '-'}
        </Typography.Text>
      )
    },
    {
      title: 'Vendor Type',
      dataIndex: 'vendorType',
      key: 'vendorType',
      sorter: true,
      width: 120,
      render: (type) => {
        const typeConfig = vendorTypeObj[type] || { color: 'default', text: type }
        return (
          <Tag color={typeConfig.color}>
            {typeConfig.text}
          </Tag>
        )
      }
    },
    {
      title: 'Business Category',
      dataIndex: 'businessCategoryName',
      key: 'businessCategoryName',
      width: 180,
      sorter: false,
      render: (text) => (
        <Tooltip title={text}>
          <Typography.Text>
            {text || '-'}
          </Typography.Text>
        </Tooltip>
      )
    },
    {
      title: 'Business Activity',
      dataIndex: 'businessActivity',
      key: 'businessActivity',
      sorter: true,
      width: 180,
      render: (text) => (
        <Tooltip title={text}>
          <Typography.Text ellipsis style={{ maxWidth: 160 }}>
              {text.length > 5 ? 
                  text.substring(0, 5) + '...' : 
                  text 
              }
          </Typography.Text>
        </Tooltip>
      )
    },
    {
      title: 'Person in Charge',
      key: 'personInCharge',
      width: 200,
      render: (_, record) => (
        <div>
           <Tooltip title={record.personInChargeName}>
            <Typography.Text strong>
              {record.personInChargeName.length > 5 ? 
                  record.personInChargeName.substring(0, 5) + '...' : 
                  record.personInChargeName
              }
            </Typography.Text>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Contact Info',
      key: 'contactInfo',
      width: 180,
      render: (_, record) => (
        <div>
          <Typography.Text>
            {record?.personInChargeContact || '-'}
          </Typography.Text>
        </div>
      )
    },
    {
      title: 'Email',
      key: 'email',
      width: 180,
      render: (_, record) => (
        <div>
          <Tooltip title={record?.personInChargeEmail }>
            <Typography.Text>
              {record?.personInChargeEmail || '-'}
            </Typography.Text>
          </Tooltip>
        </div>
      )
    },
    {
      title: 'Location',
      key: 'location',
      width: 150,
      render: (_, record) => (
        <div>
          <Tooltip title={record.location}>
            <Typography.Text strong style={{ fontSize: '12px' }}>
              {record.location.length > 5 ? 
                record.location.substring(0, 5) + '...' : 
                record.location
              }
            </Typography.Text>
          </Tooltip>
        </div>
      )

    },
    {
      title: 'City',
      key: 'city',
      width: 150,
      render: (_, record) => (
        <div>
            <Typography.Text  strong style={{ fontSize: '12px' }}>
               {record.city}
            </Typography.Text>
        </div>
      )
    },
    {
      title: 'Branches',
      dataIndex: 'numberOfBranches',
      key: 'numberOfBranches',
      sorter: true,
      width: 100,
      align: 'center',
      render: (count) => (
        <Tag color="#176B53">
          {count || 0}
        </Tag>
      )
    },
    {
      title: 'Chiller Dimensions(CM)',
      dataIndex: 'chillerDimensions',
      key: 'chillerDimensions',
      width: 150,
      render: (dimensions, record) => {
        // Only show for host type vendors
        if (record.vendorType === 'host' && dimensions) {
          return (
            <Typography.Text>
              <span>l:{dimensions.lengthofChiler}</span>, <span>h:{dimensions.heightofChiler}</span>, <span>w:{dimensions.widthofChiler}</span>
            </Typography.Text>
          )
        }
        return '-'
      }
    },
    {
      title: 'Chiller Image',
      dataIndex: 'chillerImage',
      key: 'chillerImage',
      width: 150,
      render: (dimensions, record) => {
        // Only show for host type vendors
        if (record.vendorType === 'host') {
          const url =  config.AWS_URL + record.chillerImage
          return (
            <Typography.Text>
              <button onClick={() => handleOpen(url)} className='text-blue-400 hover:underline cursor-pointer'>
                Click here to view chiller image
              </button>
            </Typography.Text>
          )
        }
        return '-'
      }
    },  
    {
      title: 'Store Front Image',
      dataIndex: 'storeFrontImage',
      key: 'storeFrontImage',
      width: 150,
      render: (dimensions, record) => {
        // Only show for host type vendors
        if (record.vendorType === 'host') {
          const url =  config.AWS_URL + record.storefrontImage
          return (
            <Typography.Text>
              <button onClick={() => handleOpen(url)} className='text-blue-400 hover:underline cursor-pointer'>
                Click here to view store front image
              </button>
            </Typography.Text>
          )
        }
        return '-'
        }
      },
    {
      title: 'Enquiry Requested Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      sorter: true,
      width: 120,
      render: (date) => (
        <Typography.Text>
          {date ? new Date(date).toLocaleDateString() : '-'}
        </Typography.Text>
      )
    }
  ]

  return (
    <Card>
      <div style={{ marginBottom: 16 }}>
        <Title level={4} style={{ marginBottom: 16 }}>
          Vendor Enquiries
        </Title>
      </div>
      {
        isModalVisible && (
          <ImageViewerModal
            mainSrc={selectedImageUrl}
            handleClose={handleClose}
            isModalVisible={isModalVisible}
            setIsModalVisible={setIsModalVisible}
          />
        )
      }

      <Table
        columns={columns}
        dataSource={Array.isArray(data) ? data : []} 
        rowKey={(record) => record.id || record._id}
        pagination={pagination}
        loading={loading}
        onChange={handleTableChange}
        scroll={{ x: 1500 }}
        size="middle"
        bordered
      />
    </Card>
  )
}

export default VendorEnquiryList